import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { InputComponent } from 'src/app/components/input/input.component';
import { AuthenticationService, SsoButton, ssoButton } from 'src/app/services/authentication.service';
import { MessageService } from 'src/app/services/message.service';
import { UtilsService } from 'src/app/services/utils.service';

/** The e-mail comes first: it decides whether the page asks for a password or sends to a directory. */
type LoginStep = 'email' | 'password' | 'sso';

const REMEMBERED_EMAIL = 'uzai-login-email';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  loading: boolean = false;
  step: LoginStep = 'email';
  email?: string;
  password?: string;
  sso?: SsoButton;

  @ViewChild('emailInput') emailInput?: InputComponent;
  @ViewChild('passwordInput') passwordInput?: InputComponent;

  errors: any = {
    email: {
      value: false,
      message: undefined
    },
    password: {
      value: false,
      message: undefined
    }
  }

  constructor(
    private _message: MessageService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    private utils: UtilsService
  ) {

  }

  ngOnInit() {
    this.email = this.remembered() ?? undefined;
    // A refused directory sign-in comes back here with its identifier in the URL.
    const error = this.route.snapshot.queryParamMap.get('error');
    if (error) {
      // The browser arrives here by a full page load: wait for the translations before looking the message up.
      const key = `ERRORS.${error}`;
      this.translate.get(key).subscribe((text: string) => {
        this.toastr.error(text !== key ? key : 'ERRORS.GENERIC', undefined, { disableTimeOut: true });
      });
      this.router.navigate([], { queryParams: {}, replaceUrl: true, state: window.history.state });
    }
  }

  private remembered() {
    try {
      return sessionStorage.getItem(REMEMBERED_EMAIL);
    } catch (err) {
      return null;
    }
  }

  private remember(email: string) {
    try {
      sessionStorage.setItem(REMEMBERED_EMAIL, email);
    } catch (err) {
      // Private browsing or blocked storage: the e-mail is simply not pre-filled next time.
    }
  }

  private requested(): string {
    return window.history.state?.requested || '/';
  }

  _hasEmailErrors() {
    this.errors.email = {
      value: false,
      message: undefined
    }
    if (!this.email) {
      this.errors.email.value = true;
      this.errors.email.message = this.translate.instant('ERRORS.EMAIL_REQUIRED');
    } else {
      if (!this.utils.validateEmailPattern(this.email)) {
        this.errors.email.value = true;
        this.errors.email.message = this.translate.instant('ERRORS.EMAIL_FORMAT');
      }
    }
    return this.errors.email.value;
  }

  _hasErrors() {
    const email = this._hasEmailErrors();
    this.errors.password = {
      value: false,
      message: undefined
    }
    if (!this.password) {
      this.errors.password.value = true;
      this.errors.password.message = this.translate.instant('ERRORS.PASSWORD_REQUIRED');
    }
    return email || this.errors.password.value;
  }

  /**
   * The input component hands its value over 100 ms after the last keystroke: someone who types and
   * presses Enter at once would be told the field is empty. Read what is in the fields now.
   */
  private readFields() {
    if (this.emailInput && typeof this.emailInput.value === 'string') {
      this.email = this.emailInput.value;
    }
    if (this.passwordInput && typeof this.passwordInput.value === 'string') {
      this.password = this.passwordInput.value;
    }
  }

  /** The Enter key does what the visible button does. */
  submit() {
    if (this.loading) {
      return;
    }
    if (this.step === 'email') {
      this.next();
    } else if (this.step === 'password') {
      this.login();
    } else {
      this.continueWithSso();
    }
  }

  async next() {
    this.readFields();
    if (this._hasEmailErrors()) {
      return;
    }
    const email = (this.email as string).trim();
    try {
      this.loading = true;
      this._message.emitChange("LOADING", "START");
      const found = await this.authService.discover(email);
      this.remember(email);
      if (found.mode === 'sso') {
        this.sso = ssoButton(found);
        this.step = 'sso';
      } else {
        this.sso = undefined;
        this.step = 'password';
      }
    } catch (err: any) {
      if (err?.status === 429) {
        this.toastr.error('ERRORS.TOO_MANY_REQUESTS');
      } else {
        // The question could not be asked: the password must stay reachable.
        this.sso = undefined;
        this.step = 'password';
      }
    } finally {
      this.loading = false;
      this._message.emitChange("LOADING", "END");
    }
  }

  continueWithSso() {
    if (!this.sso) {
      return;
    }
    this.loading = true;
    this._message.emitChange("LOADING", "START");
    window.location.assign(this.authService.ssoStartUrl(this.sso.domain, this.requested()));
  }

  usePassword() {
    this.step = 'password';
  }

  changeEmail() {
    this.step = 'email';
    this.sso = undefined;
    this.password = undefined;
    this.errors.password = { value: false, message: undefined };
  }

  async login() {
    this.readFields();
    if (this._hasErrors()) {
      return;
    } else {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        const requested = this.requested();
        const res = await firstValueFrom(this.authService.login(this.email as string, this.password as string));
        this.translate.use(res.lang);
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.router.navigate([requested]);
        this.toastr.info(this.translate.instant("LOGIN.GOOD_TO_SEE_YOU_BACK", res), this.translate.instant("LOGIN.TITLE"));

      } catch (err: any) {
        console.log(err)
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        if (err.message === "WRONG_LOGIN_OR_PASSWORD") {
          this.errors.password.value = true;
          this.errors.password.message = this.translate.instant(`ERRORS.${err.message}`);
        } else if (err.message === "TOO_MANY_WRONG_LOGIN_ATTEMPTS") {
          this.errors.password.value = true;
          this.errors.password.message = this.translate.instant(`ERRORS.${err.message}`);
        } else if (err.message === "USER_SUSPENDED") {
          this.toastr.error(`ERRORS.USER_SUSPENDED`, undefined, { disableTimeOut: true })
        } else if (err.message === "SSO_REQUIRED") {
          // The organisation enforces its directory: show that path instead of the password.
          this.password = undefined;
          this.toastr.info(`ERRORS.SSO_REQUIRED`);
          await this.next();
        } else {
          if (err && err.message && this.utils.hasTranslation(`ERRORS.${err.message}`)) {
            this.toastr.error(`ERRORS.${err.message}`)
          } else {
            this.toastr.error(`ERRORS.GENERIC`)
          }
        }
      }
    }


  }
}
